import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import PortalHeader from '../../components/Portal/PortalHeader';
import WaButton from '../../components/wa/WaButton';
import StudentRecaps from '../../components/Portal/StudentRecaps';
import CalendarSyncCard from './CalendarSyncCard';
import {
	fetchStudentsForPrincipal,
	isStudentPrincipal,
} from '../../services/principalStudents';
import { fetchTimeSlotsForStudents } from '../../services/timeSlotService';
import { cancelLesson } from '../../services/makeupService';
import { expandUpcomingLessons, ymd } from '../../utils/occurrenceExpansion';
import { formatTime12Hour } from '../../utils/schedulingUtils';
import { LOCATIONS } from '../EmployeePortal/Scheduling/locations';
import { importantDatesInWindow, seasonLookaheadDays } from '../../data/seasonData';

// Customer schedule view (Track 2 Slice B read + Slice C actions — fall model
// §4). A claimed parent sees their family's placed weekly lessons and each
// student's makeup-credit count. Reads ONLY the two familyId-scoped grants the
// rules give a customer, and the queries must match those rule predicates
// exactly (rules are not filters):
//   1. students  where familyId == users.familyId
//   2. timeSlots where studentId in [that family's studentIds]
// Occurrences come from the shared expander (utils/occurrenceExpansion.js) —
// the same engine the staff Calendar renders with, so a cancelled/rescheduled
// exception reads identically on both surfaces.
//
// Slice C v1.1 actions (unified 24h rule — reverses decision 5): EVERY
// upcoming owned occurrence gets a cancel affordance, recurring AND redeemed
// makeups / one-time lessons, regardless of the 24h window. For a single-date
// slot the cancel passes date = specificDate (its _originDate). The server
// still decides the real outcome and the toast reports it; the dialog only
// WARNS inside 24h (deterministic policy: no credit — a makeup's credit is
// forfeited) and offers an optional free-text note that rides the owner push.
// Redeem lives on the /portal/customer/makeup surface; "Schedule a makeup"
// here is disabled at zero family-wide credits.
//
// VIEW TABS (2026-08-12). The page used to stack both halves vertically —
// every student's schedule, and under each one that student's past lessons
// and recaps — so reaching a recap meant scrolling past the whole schedule,
// and getting back meant scrolling all the way up again. The two halves are
// now separate VIEWS behind one segmented control: "Scheduled" (default, per
// Anthony) and "Past lessons". Deliberately ONE control at page level rather
// than a tab strip inside each student card: a two-child family would
// otherwise get two independent controls and keep exactly the scrolling
// problem the tabs exist to remove. The control is rendered only once the
// family's students have loaded, so it never appears above an error or an
// unlinked-account message.

const VIEWS = [
	{ key: 'scheduled', label: 'Scheduled', icon: 'fa-calendar-week' },
	{ key: 'past', label: 'Past lessons', icon: 'fa-comment-lines' },
];

const DOW_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MON_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// How many occurrences a student card shows before "Show all". The window
// itself now runs to the end of the season (seasonLookaheadDays) rather than a
// fixed 60 days — see the helper in data/seasonData.js for why that number was
// wrong. The two are deliberately separate concerns: the WINDOW decides what a
// parent can reach (and therefore cancel), the CAP decides how much lands on
// screen at once. Collapsing them into one number is what made a December
// lesson unreachable, and simply raising it would render ~33 rows on a phone —
// the "excessive scrolling" flagged in the same review that flagged this.
//
// Four stays the default because the review called that summary out as the part
// that works: "the scheduling page summary that lists the next three or four
// upcoming lessons is well-received."
const INITIAL_OCCURRENCES = 4;

const locationName = (value) => {
	const match = LOCATIONS.find((l) => l.value === value);
	return match ? match.name : value || '';
};
// Location colors come from CSS TOKENS here, not from the employee palette's
// literal hexes: those are tuned for a light chip on white and all but vanish on
// the Comfort-Dark card. The token's light value IS the palette value, so the
// default and Comfort themes are unchanged; only the dark theme re-points it.
// See src/styles/tokens/colors.css and locationPalette.test.js.
const LOCATION_TOKENS = {
	Lakeville: { accent: 'var(--loc-lakeville-accent, #1d83d4)', text: 'var(--loc-lakeville-text, #1565a6)' },
	HLDC: { accent: 'var(--loc-hldc-accent, #e0699a)', text: 'var(--loc-hldc-text, #b03f78)' },
};
const locationStyle = (value) => LOCATION_TOKENS[value] || null;

const studentDisplayName = (s) =>
	s.fullName || [s.firstName, s.lastName].filter(Boolean).join(' ') || s.name || s.studentName || 'Student';

const occurrenceDateLabel = (d) =>
	`${DOW_FULL[d.getDay()]}, ${MON_ABBR[d.getMonth()]} ${d.getDate()}`;

const parseYmd = (s) => new Date(s + 'T00:00:00');

const shortDate = (s) => {
	const d = parseYmd(s);
	return `${MON_ABBR[d.getMonth()]} ${d.getDate()}`;
};

// Season-date label: a single day for a recital, an inclusive range for a
// closure ("Nov 25–29", or "Dec 21 – Jan 3" across months).
const importantDateLabel = (entry) => {
	if (entry.start === entry.end) return shortDate(entry.start);
	const a = parseYmd(entry.start);
	const b = parseYmd(entry.end);
	return a.getMonth() === b.getMonth()
		? `${MON_ABBR[a.getMonth()]} ${a.getDate()}–${b.getDate()}`
		: `${shortDate(entry.start)} – ${shortDate(entry.end)}`;
};

// Display-only 24h check for the confirm-dialog copy (device clock ≈ studio
// time for our local families). The SERVER still decides the real outcome —
// this only picks which warning the dialog shows; inside 24h the policy is
// deterministic ("no credit"), so the warning is honest.
const isInside24h = (o) =>
	new Date(`${ymd(o._date)}T${o.startTime || '00:00'}:00`).getTime() - Date.now() < 24 * 3600000;

const MON_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
	'July', 'August', 'September', 'October', 'November', 'December'];

// A closure earns a row on a student's card ONLY when it actually removes one
// of THEIR lessons. Closures are studio-wide, but a Monday holiday changes
// nothing for a Tuesday student — and rendering it anyway is exactly the
// signal-to-noise problem this page was reviewed for (2026-08-20: a Tuesday
// card rendered four closures, two of which displaced nothing).
// RECITALS ARE EXEMPT — a recital is an event the family ATTENDS, not an
// absence, so it always renders regardless of the student's lesson day.
export const closureDisplaces = (entry, weeklySlots) => {
	if (!weeklySlots.length) return false;
	const days = new Set(weeklySlots.map((s) => s.dayOfWeek));
	const cursor = parseYmd(entry.start);
	const end = parseYmd(entry.end);
	while (cursor <= end) {
		if (days.has(cursor.getDay())) return true;
		cursor.setDate(cursor.getDate() + 1);
	}
	return false;
};

// Relative distance, shown on the NEXT row only. Compares local start-of-day
// to local start-of-day so it can never drift by a timezone hour, and it is
// display-only — nothing branches on it.
export const relativeDayLabel = (target, from) => {
	const a = new Date(from);
	a.setHours(0, 0, 0, 0);
	const b = new Date(target);
	b.setHours(0, 0, 0, 0);
	const days = Math.round((b - a) / 86400000);
	if (days <= 0) return 'today';
	if (days === 1) return 'tomorrow';
	if (days < 14) return `in ${days} days`;
	return `in ${Math.round(days / 7)} weeks`;
};

// Voice is 45 minutes, piano and guitar 30 — a real difference to a parent, so
// the summary band states it rather than making them subtract two clock times.
const durationLabel = (s) => {
	if (!s.startTime || !s.endTime) return '';
	const [h1, m1] = s.startTime.split(':').map(Number);
	const [h2, m2] = s.endTime.split(':').map(Number);
	const mins = h2 * 60 + m2 - (h1 * 60 + m1);
	return mins > 0 ? `${mins} min` : '';
};

// A row shows chips ONLY when it departs from the summary band above it. That
// is the whole mechanism behind "the row is a date": exceptions are legible
// BECAUSE the default carries nothing.
const occurrenceDeviates = (o, weeklySlots) => {
	if (o.isMakeup) return true;
	const rule = weeklySlots.find((s) => s.id === o._slotId) || weeklySlots[0];
	if (!rule) return true;
	return o.startTime !== rule.startTime || o.location !== rule.location;
};

const NOTE_MAX_LENGTH = 500;

const MyLessons = () => {
	const { userProfile } = useAuth();
	const familyId = userProfile?.familyId || null;
	// P2b: a student principal holds BOTH scalars, so familyId alone no longer
	// identifies the read. Depend on the primitives, not on userProfile's
	// object identity, and let the resolver issue the per-principal query.
	const studentId = userProfile?.studentId || null;
	const role = userProfile?.role || null;

	/* P2b — CANCELLING IS A GUARDIAN ACT, AND THE SERVER ALREADY SAYS SO.
	   requireMakeupCaller (functions/index.js) admits admin, owner and
	   role:'customer' only, so a student calling cancelLesson gets a flat
	   permission-denied. Rendering the control anyway would offer a button that
	   is guaranteed to fail — worse than absent, because it reads as a broken
	   feature rather than one the family has not been given.

	   This is the DEFAULT-OFF half of Anthony's 2026-08-18 ask: some students
	   are old enough that their parents would allow this, so a per-student
	   opt-in is wanted. That toggle is its own card — it needs a field on the
	   student, an admin control, and a change to requireMakeupCaller, which is
	   a functions deploy. Until it exists the honest default is: not offered.
	   When it does exist, this constant becomes the toggle's value. */
	const canCancel = !isStudentPrincipal(userProfile);

	const [students, setStudents] = useState([]);
	const [slots, setSlots] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);

	// Which half of the page is showing. Scheduled is the landing view every
	// time — a parent arriving here almost always wants "when is the next
	// lesson", and Anthony asked for it explicitly.
	const [view, setView] = useState('scheduled');

	// Cancel-confirm dialog: holds the occurrence being cancelled (or null).
	const [confirmCancel, setConfirmCancel] = useState(null);
	const [cancelBusy, setCancelBusy] = useState(false);
	// Optional free-text note (deliberately ambiguous copy — no promise it
	// changes anything). Reset every time the dialog opens.
	const [cancelNote, setCancelNote] = useState('');
	const dialogRef = useRef(null);
	useEffect(() => {
		if (dialogRef.current) dialogRef.current.open = !!confirmCancel;
	}, [confirmCancel]);
	// React 18 doesn't wire the custom element's events; attach the WA close
	// event explicitly so ESC/backdrop closes don't leave state stale.
	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return undefined;
		const onHide = () => setConfirmCancel(null);
		el.addEventListener('wa-after-hide', onHide);
		return () => el.removeEventListener('wa-after-hide', onHide);
	}, []);

	// Per-student "show the rest of the season". Keyed by student id so a
	// multi-student family expands one child without unfolding the others.
	const [showAllFor, setShowAllFor] = useState(() => new Set());
	const toggleShowAll = (studentId) =>
		setShowAllFor((prev) => {
			const next = new Set(prev);
			if (next.has(studentId)) next.delete(studentId);
			else next.add(studentId);
			return next;
		});

	const [toast, setToast] = useState(null);
	const toastTimer = useRef(null);
	useEffect(() => () => clearTimeout(toastTimer.current), []);
	const showToast = (msg) => {
		setToast(msg);
		clearTimeout(toastTimer.current);
		toastTimer.current = setTimeout(() => setToast(null), 6000);
	};

	// The server decides the 24h outcome; the toast reports what it decided.
	const performCancel = async () => {
		if (!confirmCancel || cancelBusy) return;
		const { _slotId: slotId, _originDate: date } = confirmCancel;
		setCancelBusy(true);
		try {
			const result = await cancelLesson(slotId, date, cancelNote.trim() || undefined);
			if (result.outcome === 'credit_issued') {
				showToast('Lesson cancelled. A makeup credit was added.');
			} else if (result.outcome === 'no_credit') {
				showToast('Lesson cancelled. This was within 24 hours, so no makeup credit per policy.');
			} else {
				// already_cancelled — idempotent no-op; never toast a credit.
				showToast('This lesson was already cancelled.');
			}
			// Drop the occurrence locally the same way the server did (an
			// exceptions map entry) and reflect a fresh credit on the badge.
			setSlots((prev) => prev.map((s) => (s.id === slotId
				? { ...s, exceptions: { ...(s.exceptions || {}), [date]: { cancelled: true } } }
				: s)));
			if (result.outcome === 'credit_issued' && result.studentId) {
				setStudents((prev) => prev.map((st) => (st.id === result.studentId
					? { ...st, makeupBalance: (st.makeupBalance || 0) + 1 }
					: st)));
			}
			setConfirmCancel(null);
		} catch (err) {
			console.error('cancelLesson failed:', err);
			showToast("We couldn't cancel that lesson. Please try again, or contact us.");
		} finally {
			setCancelBusy(false);
		}
	};

	useEffect(() => {
		if (!familyId) {
			setLoading(false);
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				const kids = await fetchStudentsForPrincipal({ role, familyId, studentId });
				const fetched = await fetchTimeSlotsForStudents(kids.map((k) => k.id));
				if (cancelled) return;
				setStudents(kids);
				// fetchTimeSlotsForStudents already constrains status=='assigned'
				// in the query (it has to — the customer rule denies provisional
				// holds, and rules are not filters). Kept as a defensive filter.
				setSlots(fetched.filter((s) => s.status === 'assigned'));
			} catch (err) {
				console.error('Error loading family schedule:', err);
				if (!cancelled) setLoadError(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [role, familyId, studentId]);

	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const todayStr = ymd(today);
	const lookaheadDays = seasonLookaheadDays(today);
	const upcoming = expandUpcomingLessons(slots, today, lookaheadDays);
	// Studio-wide closures + recitals in the same window — interleaved into each
	// student's Upcoming list so a gap between lessons reads as "closed for X,"
	// not an unexplained blank. Studio-wide, so a multi-student family sees them
	// on each card (each card stays self-contained — accepted duplication).
	const importantDates = importantDatesInWindow(today, lookaheadDays);


	const scheduleCardFor = (student) => {
		const own = slots.filter((s) => s.studentId === student.id);
		const weekly = own.filter((s) => s.isRecurring);
		const allOccurrences = upcoming.filter((o) => o.studentId === student.id);
		const showAll = showAllFor.has(student.id);
		const occurrences = showAll ? allOccurrences : allOccurrences.slice(0, INITIAL_OCCURRENCES);
		const credits = student.makeupBalance || 0;

		// Merge studio closures/recitals that fall within the shown lesson span
		// into the Upcoming list, sorted by date. Only interleaved when the
		// student has upcoming lessons (the section is gated on that), so an
		// unplaced student never shows a bare wall of closures.
		const lastShownStr = occurrences.length ? ymd(occurrences[occurrences.length - 1]._date) : todayStr;
		const seasonRows = importantDates.filter(
			(e) =>
				e.start >= todayStr &&
				e.start <= lastShownStr &&
				(e.type === 'recital' || closureDisplaces(e, weekly))
		);
		const rows = [
			...occurrences.map((o) => ({ kind: 'lesson', sort: ymd(o._date), o })),
			...seasonRows.map((e) => ({ kind: 'season', sort: e.start, e })),
		].sort((a, b) => a.sort.localeCompare(b.sort));

		// Flatten to what actually renders: month dividers (expanded state only —
		// 31 rows is a wall without them and they cost nothing when collapsed) and
		// a NEXT mark on the first LESSON row, which is not necessarily the first
		// row, because a closure or recital can sort ahead of it.
		const displayRows = [];
		let lastMonth = null;
		let nextMarked = false;
		rows.forEach((row) => {
			const when = row.kind === 'lesson' ? row.o._date : parseYmd(row.e.start);
			const mk = `${when.getFullYear()}-${when.getMonth()}`;
			if (showAll && mk !== lastMonth) {
				displayRows.push({ kind: 'mdiv', mk, label: MON_FULL[when.getMonth()] });
				lastMonth = mk;
			}
			if (row.kind === 'lesson' && !nextMarked) {
				nextMarked = true;
				displayRows.push({ ...row, isNext: true });
				return;
			}
			displayRows.push(row);
		});

		return (
			<wa-card key={student.id} className='cust-sched-card'>
				{/* Header group: name + credit badge on one line, the makeup action
				    directly under it. Reads as ONE unit above the schedule rows —
				    at 375px the old single wrapping flex row broke into a ragged
				    three-line stack with the button orphaned mid-card. */}
				<div className='cust-sched-head'>
					<div className='cust-sched-head__id'>
						<h2 className='cust-sched-name'>{studentDisplayName(student)}</h2>
						{/* Problem 3: at zero credits there is NOTHING here — no badge and
						    no button. A dead control is worse than no control, and zero is
						    the normal state. The earn-path is the policy line at the foot
						    of the card, which every parent sees regardless. */}
						{credits > 0 && (
							<wa-badge variant='success'>
								{credits} makeup {credits === 1 ? 'lesson' : 'lessons'}
							</wa-badge>
						)}
				</div>

				{/* The makeup module appears ONLY when this student has something to
				    spend. Per-student rather than family-wide (a change): the copy
				    names the child, and "Max has 0 makeup lessons ready to book" is
				    not a sentence worth rendering. Credits are counted in LESSONS —
				    never dollars — and the copy says so. */}
				{credits > 0 && (
					<div className='cust-sched-credit'>
						<span className='cust-sched-credit__text'>
							{studentDisplayName(student)} has {credits} makeup {credits === 1 ? 'lesson' : 'lessons'} ready to book.
						</span>
						<Link to='/portal/customer/makeup' className='cust-sched-head__action'>
							<WaButton size='small' variant='brand' appearance='accent'>
								<i className='fa-sharp fa-solid fa-calendar-plus' style={{ marginRight: '0.4rem' }}></i>
								Book a makeup
							</WaButton>
						</Link>
					</div>
				)}
				</div>

				{/* THE GROUP (problem 5). The recurring rule and the dates it
				    produces used to read as two unrelated blocks. One border around
				    both makes the cream band the LIST'S HEADER: "these dates are
				    instances of that rule." Everything below inherits its context
				    from it, which is what lets a row carry a date and nothing else. */}
				{(weekly.length > 0 || occurrences.length > 0) && (
					<div className='cust-sched-group'>
						{weekly.map((s) => {
							const lc = locationStyle(s.location);
							const startsLater = s.startDate && s.startDate > todayStr;
							const meta = [
								s.instrument,
								durationLabel(s),
								locationName(s.location),
								startsLater ? `starts ${occurrenceDateLabel(parseYmd(s.startDate))}` : null,
							].filter(Boolean).join(' \u00b7 ');
							return (
								<div key={s.id} className='cust-sched-sum'>
									<div className='cust-sched-sum__rule'>
										<i className='fa-sharp fa-solid fa-calendar-week' style={{ marginRight: '0.5rem', color: lc?.accent || 'var(--text-heading, #384971)' }}></i>
										{DOW_FULL[s.dayOfWeek]}s, {formatTime12Hour(s.startTime)}–{formatTime12Hour(s.endTime)}
									</div>
									{meta && <div className='cust-sched-sum__meta'>{meta}</div>}
								</div>
							);
						})}

						{displayRows.map((row) => {
							if (row.kind === 'mdiv') {
								return <div key={`m-${row.mk}`} className='cust-sched-mdiv'>{row.label}</div>;
							}
							if (row.kind === 'season') {
								/* Problem 4: a closure is an ABSENCE and must not weigh the
								   same as a lesson; a recital is the opposite of an absence,
								   so it takes the gold wash rather than the grey. */
								const isRecital = row.e.type === 'recital';
								return (
									<div
										key={`season-${row.e.type}-${row.e.start}`}
										className={`cust-sched-season${isRecital ? ' cust-sched-season--recital' : ''}`}>
										<span className='cust-sched-season__label'>
											<i className={`fa-sharp fa-solid ${isRecital ? 'fa-star' : 'fa-calendar-xmark'}`} style={{ marginRight: '0.4rem' }}></i>
											{isRecital ? row.e.name : 'Studio closed'}
										</span>
										<span>
											{importantDateLabel(row.e)}
											{!isRecital && row.e.name ? ` \u00b7 ${row.e.name}` : ''}
											{isRecital && row.e.location ? ` \u00b7 ${row.e.location}` : ''}
										</span>
									</div>
								);
							}
							const o = row.o;
							const deviates = occurrenceDeviates(o, weekly);
							return (
								<div
									key={`${o._slotId}-${o._originDate}`}
									className={`cust-sched-row${row.isNext ? ' cust-sched-row--next' : ''}`}>
									{row.isNext && <span className='cust-sched-nexttag'>Next</span>}
									<span className='cust-sched-row__date'>{occurrenceDateLabel(o._date)}</span>
									{row.isNext && <span className='cust-sched-rel'>{relativeDayLabel(o._date, today)}</span>}
									{/* Chips ONLY on deviation — a row that matches the summary
									    band above it says nothing, which is what makes the
									    exceptions readable. The Makeup badge keeps its shipped
									    orange pairing (5.84:1, documented below its original). */}
									{deviates && !o.isMakeup && (
										<span className='cust-sched-chip'>
											{formatTime12Hour(o.startTime)} · {locationName(o.location)}
										</span>
									)}
									{o.isMakeup && (
										<>
											<span className='cust-sched-chip'>
												{formatTime12Hour(o.startTime)} · {locationName(o.location)}
											</span>
											<wa-badge
												appearance='filled'
												style={{
													'--wa-color-fill-normal': 'var(--wa-color-orange-90)',
													'--wa-color-on-normal': 'var(--wa-color-orange-20)',
												}}>
												Makeup
											</wa-badge>
										</>
									)}
									{/* Problem 2: Cancel is DEMOTED, never hidden. It cannot be a
									    hover/swipe/overflow affordance — touch has no hover at
									    all on the installed PWA, and a hover-only control is the
									    hardest thing to find for a low-vision reader. So it stays
									    on every row, quieted by colour and weight. Gated on
									    canCancel: a student is never offered a control the server
									    would refuse. */}
									{canCancel && (
										<button
											type='button'
											className='cust-sched-cancel'
											onClick={() => {
												setCancelNote('');
												setConfirmCancel({ ...o, _studentLabel: studentDisplayName(student) });
											}}>
											Cancel
										</button>
									)}
								</div>
							);
						})}

						{/* Reaching the rest of the season is what makes a December
						    lesson cancellable at all — the default four is the summary,
						    not the limit. */}
						{allOccurrences.length > INITIAL_OCCURRENCES && (
							<button
								type='button'
								className='cust-sched-more'
								aria-expanded={showAll}
								onClick={() => toggleShowAll(student.id)}>
								{showAll ? 'Show fewer' : `Show all ${allOccurrences.length} lessons`}
							</button>
						)}
					</div>
				)}

				{/* The common ONBOARDING state: a claimed account whose weekly time
				    is not placed yet. Studio voice, no policy line, nothing to expand
				    — there is no schedule to explain the rules of. */}
				{weekly.length === 0 && own.length === 0 && (
					<div className='cust-sched-place'>
						<i className='fa-sharp fa-solid fa-music cust-sched-place__icon' aria-hidden='true'></i>
						<span>
							{studentDisplayName(student)}’s weekly lesson time isn’t set yet — we’re finding their spot and we’ll reach out as soon as it’s ready.
						</span>
					</div>
				)}

				{/* One quiet policy line — replaces the three-line "Life happens…"
				    explainer that used to sit above the schedule rows. Muted body
				    text (not a callout box); the full rules live on /policies. */}
				<p className='cust-sched-policy'>
					Cancel 24+ hours ahead to save the lesson as a makeup.{' '}
					<Link to='/policies#makeups'>Learn more</Link>
				</p>
			</wa-card>
		);
	};

	// Past lessons — the family-facing Lesson Recap read surface. Sourced from
	// the recap docs themselves, NOT from `lessons`: that collection's customer
	// rule branch keys on a legacy `userId` field no imported lesson carries,
	// and this page has never queried it. Only the student's NAME rides above
	// it here — the credit badge and "Schedule a makeup" belong with the
	// schedule, and repeating them under a tab about the past is misdirection.
	const pastCardFor = (student) => {
		const name = studentDisplayName(student);
		return (
			<wa-card key={student.id} className='cust-sched-card'>
				<div className='cust-sched-head'>
					<div className='cust-sched-head__id'>
						<h2 className='cust-sched-name'>{name}</h2>
					</div>
				</div>
				<StudentRecaps
					studentId={student.id}
					studentName={name}
					showHeading={false}
					studentOnly={isStudentPrincipal(userProfile)}
					emptyMessage={isStudentPrincipal(userProfile)
						? 'No lesson recaps yet. After a lesson, your teacher\'s recap will show up here.'
						: `No lesson recaps for ${name} yet. After a lesson, your teacher's recap and any notes for parents will show up here.`}
				/>
			</wa-card>
		);
	};

	return (
		<>
			<PortalHeader
				title='Schedule'
				subtitle='Your weekly lessons and makeup credits.'
			/>
			<div style={{ maxWidth: 760, margin: '0 auto', padding: '1.5rem 1rem 3rem' }}>
				{loading && <p style={{ textAlign: 'center', padding: '2rem 0' }}>Loading your schedule…</p>}

				{!loading && loadError && (
					<wa-card style={{ display: 'block' }}>
						<p style={{ margin: 0 }}>
							We couldn't load your schedule just now. Please refresh, or{' '}
							<a href='/contact'>contact us</a> if it keeps happening.
						</p>
					</wa-card>
				)}

				{!loading && !loadError && !familyId && (
					<wa-card style={{ display: 'block' }}>
						<p style={{ margin: 0 }}>
							Your account isn't linked to a family yet, so there's no schedule to
							show. If you think this is a mistake, <a href='/contact'>contact us</a>{' '}
							and we'll get you set up.
						</p>
					</wa-card>
				)}

				{!loading && !loadError && familyId && students.length === 0 && (
					<wa-card style={{ display: 'block' }}>
						<p style={{ margin: 0 }}>
							No students are linked to your account yet. If that doesn't look
							right, <a href='/contact'>contact us</a>.
						</p>
					</wa-card>
				)}

				{!loading && !loadError && students.length > 0 && (
					<>
						{/* The switch between the two halves. Plain buttons with
						    tablist semantics, following the SongTabs precedent —
						    wa-tab-group has zero in-app usage and a two-segment
						    control is simpler as buttons. Both segments are always
						    present and always visibly labelled: a control that hid
						    "Past lessons" whenever a family had none would answer
						    "where did my recaps go?" with silence. */}
						<div className='cust-viewtabs' role='tablist' aria-label='Schedule views'>
							{VIEWS.map((v) => {
								const on = view === v.key;
								return (
									<button
										key={v.key}
										type='button'
										role='tab'
										id={`cust-viewtab-${v.key}`}
										aria-selected={on}
										aria-controls='cust-viewpanel'
										className={`cust-viewtabs__tab${on ? ' is-active' : ''}`}
										onClick={() => setView(v.key)}>
										<i className={`fa-sharp fa-solid ${v.icon}`} aria-hidden='true' />
										<span>{v.label}</span>
									</button>
								);
							})}
						</div>

						<div
							id='cust-viewpanel'
							role='tabpanel'
							aria-labelledby={`cust-viewtab-${view}`}>
							{view === 'scheduled'
								? students.map(scheduleCardFor)
								: students.map(pastCardFor)}
						</div>

						{/* The old footnote restated the 24h rule (plus the credit-return
						    detail) a second time — the per-card policy line carries that
						    now, so this is only the way to reach a human. */}
						<p className='cust-sched-footnote'>
							Questions about your schedule? <a href='/contact'>Get in touch</a>{' '}
							and we'll take care of it.
						</p>

						<CalendarSyncCard />
					</>
				)}
			</div>

			{/* Cancel-confirm dialog. The server decides the actual outcome and the
			    toast reports it — but inside 24h the policy is deterministic, so the
			    dialog warns plainly (v1.1). The note field is deliberately ambiguous:
			    no promise, no mention of credits or circumstances. */}
			<wa-dialog ref={dialogRef} label='Cancel this lesson?'>
				{confirmCancel && (
					<div>
						<p style={{ marginTop: 0 }}>
							<strong>{confirmCancel._studentLabel}</strong> —{' '}
							{occurrenceDateLabel(confirmCancel._date)},{' '}
							{formatTime12Hour(confirmCancel.startTime)}–{formatTime12Hour(confirmCancel.endTime)}{' '}
							at {locationName(confirmCancel.location)}
							{confirmCancel.isMakeup ? ' (makeup lesson)' : ''}.
						</p>
						{isInside24h(confirmCancel) ? (
							<p style={{ color: '#a33', fontWeight: 600 }}>
								This lesson is less than 24 hours away, so we won't be able to
								save it as a makeup.
								{confirmCancel.isMakeup
									? ' The credit used to book this makeup won\'t be returned.'
									: ''}
							</p>
						) : (
							<p style={{ color: '#666' }}>
								Cancellations with less than 24 hours' notice aren't eligible for
								a makeup credit.
							</p>
						)}
						<label style={{ display: 'block', marginTop: '0.75rem' }}>
							<span style={{ display: 'block', fontSize: '0.9rem', color: '#555', marginBottom: '0.3rem' }}>
								Anything you'd like us to know? (optional)
							</span>
							<textarea
								value={cancelNote}
								onChange={(e) => setCancelNote(e.target.value)}
								maxLength={NOTE_MAX_LENGTH}
								rows={3}
								style={{
									width: '100%', boxSizing: 'border-box', resize: 'vertical',
									padding: '0.5rem 0.6rem', fontSize: '0.95rem', fontFamily: 'inherit',
									border: '1px solid var(--wa-color-surface-border, #ccc)', borderRadius: 8,
								}}
							/>
						</label>
					</div>
				)}
				<div slot='footer' style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
					<WaButton appearance='outlined' disabled={cancelBusy} onClick={() => setConfirmCancel(null)}>
						Keep lesson
					</WaButton>
					<WaButton variant='danger' loading={cancelBusy} disabled={cancelBusy} onClick={performCancel}>
						Cancel lesson
					</WaButton>
				</div>
			</wa-dialog>

			{/* Result toast */}
			{toast && (
				<div style={{ position: 'fixed', left: '50%', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 84px)', transform: 'translateX(-50%)', zIndex: 1040, maxWidth: 'min(92vw, 26rem)', background: '#384971', color: '#fff', padding: '12px 18px', borderRadius: 12, boxShadow: '0 16px 34px -12px rgba(56,73,113,.6)' }}>
					<span style={{ fontSize: '13.5px', fontWeight: 500 }}>{toast}</span>
				</div>
			)}
		</>
	);
};

export default MyLessons;
